import { DotsThreeVerticalIcon } from "@phosphor-icons/react";
import { useId, useState } from "react";
import { useIntl } from "react-intl";

import { IconButton } from "@/components/IconButton/IconButton";
import { Menu, MenuItem } from "@/components/Menu";
import { rudderAnalytics, CommonTrackingEvents } from "@/lib/rudderstack";
import { type Traveller } from "@/types/types";

type Props = {
  traveller: Traveller;
  onDelete: (traveller: Traveller) => void;
  onEdit: (traveller: Traveller) => void;
};

export function TravellerActions({ traveller, onDelete, onEdit }: Props) {
  const intl = useIntl();
  const buttonId = useId();
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        aria-label={intl.formatMessage({
          defaultMessage: "Actions",
          description:
            "Bottom sheet / menu title for traveller profile actions (edit, delete)",
        })}
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
      >
        <DotsThreeVerticalIcon size={24} />
      </IconButton>

      <Menu
        id={menuId}
        triggerId={buttonId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            rudderAnalytics.track(
              CommonTrackingEvents.USER_ENTERED_EDITING_TRAVELER,
            );
            onEdit(traveller);
            setAnchorEl(null);
          }}
          label={
            <span className="text-sm font-medium">
              {intl.formatMessage({
                defaultMessage: "Edit",
                description:
                  "Generic 'Edit' button label to modify an existing item",
              })}
            </span>
          }
        />

        <MenuItem
          onClick={() => {
            rudderAnalytics.track(CommonTrackingEvents.USER_DELETED_TRAVELER);
            onDelete(traveller);
            setAnchorEl(null);
          }}
          label={
            <span className="text-sm font-medium text-semantic-error-strong">
              {intl.formatMessage({
                defaultMessage: "Delete",
                description:
                  "Generic 'Delete' button label to permanently remove an item",
              })}
            </span>
          }
        />
      </Menu>
    </>
  );
}
